/**
 * Generic multi-field search utility that allows searching across multiple properties of objects using dot notation.
 *
 * @param items - Array of items to search through
 * @param searchValue - The search term
 * @param searchFields - Array of field paths in dot notation (e.g., "status", "potential_customer.email", "advocate.name")
 * @returns Array of items that match the search criteria
 */
export const multiFieldSearch = <T>(
  items: T[],
  searchValue: string,
  searchFields: string[],
): T[] => {
  if (!searchValue.trim()) {
    return items;
  }

  const searchTerm = searchValue.trim().toLowerCase();

  return items.filter((item: T) => {
    for (const fieldPath of searchFields) {
      const fieldValue = getNestedValue(item, fieldPath);
      if (!fieldValue) continue;

      const fieldString = String(fieldValue).toLowerCase();
      if (fieldString.includes(searchTerm)) {
        return true;
      }
    }
    return false;
  });
};

/**
 * Helper function to get nested values using dot notation
 */
const getNestedValue = (obj: unknown, path: string): unknown => {
  if (!obj || !path) return undefined;
  let cur: unknown = obj;
  for (const key of path.split(".")) {
    if (cur && typeof cur === "object") {
      cur = (cur as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }
  return cur;
};
